import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Post, PostDetail, FetchPostsParams } from '@/types'
import {
  fetchFlaggedPosts,
  searchPosts,
  fetchRecentPosts,
  fetchPostDetail,
  submitValidation,
} from '@/api/client'
import { ElMessage } from 'element-plus'
import { usePagination } from '@/composables/usePagination'

export const POSTS_AUTO_REFRESH_INTERVAL_MS = 15000

export const usePostsStore = defineStore('posts', () => {
  // Состояние
  const posts = ref<Post[]>([])
  const recentPosts = ref<Post[]>([])
  const selectedPost = ref<PostDetail | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let recentPostsRequest: Promise<void> | null = null
  let refreshInterval: ReturnType<typeof setInterval> | null = null
  let refreshRequest: Promise<void> | null = null
  let requestVersion = 0

  // Пагинация
  const { page, pageSize, hasMore, offset, setHasMoreFromLength } = usePagination()

  // Фильтры
  const searchQuery = ref('')
  const categoryFilter = ref('')
  const sourceFilter = ref('')
  const statusFilter = ref('FLAGGED')
  const districtFilter = ref('')
  const dateFrom = ref('')
  const dateTo = ref('')

  // Массовое выделение
  const selectedIds = ref<Set<number>>(new Set())

  // Геттеры
  const filteredPosts = computed(() => posts.value)

  const isSelected = computed(() => (id: number) => selectedIds.value.has(id))

  // Действия
  async function loadPosts(reset = false, silent = false) {
    const version = ++requestVersion
    if (reset && !silent) {
      // page.value здесь не сбрасывается. Callers, которым нужен сброс страницы
      // (фильтры, refresh после валидации), должны явно выставить page.value = 0.
      posts.value = []
    }

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      let data: Post[]

      if (searchQuery.value) {
        data = await searchPosts(searchQuery.value, pageSize.value, offset.value)
      } else {
        const params: FetchPostsParams = {
          limit: pageSize.value,
          offset: offset.value,
        }
        if (statusFilter.value && statusFilter.value !== 'All') {
          params.status = statusFilter.value
        } else {
          params.status = 'ALL'
        }
        if (categoryFilter.value && categoryFilter.value !== 'All') {
          params.category = categoryFilter.value
        }
        if (sourceFilter.value && sourceFilter.value !== 'All') {
          params.source_id = sourceFilter.value
        }
        if (districtFilter.value && districtFilter.value !== 'All') {
          params.federal_district = districtFilter.value
        }
        if (dateFrom.value) params.date_from = dateFrom.value
        if (dateTo.value) params.date_to = dateTo.value

        data = await fetchFlaggedPosts(params)
      }

      // A filter/page action may supersede an older background refresh.
      if (version !== requestVersion) return

      if (reset) {
        posts.value = data
      } else {
        posts.value.push(...data)
      }

      setHasMoreFromLength(data.length)
    } catch (e) {
      if (!silent && version === requestVersion) {
        error.value = 'Ошибка загрузки постов'
      }
    } finally {
      if (!silent && version === requestVersion) {
        loading.value = false
      }
    }
  }

  function refreshPosts(): Promise<void> {
    if (loading.value) return Promise.resolve()
    if (refreshRequest) return refreshRequest
    refreshRequest = loadPosts(true, true).finally(() => {
      refreshRequest = null
    })
    return refreshRequest
  }

  function startAutoRefresh(intervalMs = POSTS_AUTO_REFRESH_INTERVAL_MS) {
    stopAutoRefresh()
    refreshInterval = setInterval(() => {
      if (document.hidden) return
      refreshPosts()
    }, intervalMs)
  }

  function stopAutoRefresh() {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  function loadRecentPosts(): Promise<void> {
    if (recentPostsRequest) return recentPostsRequest
    recentPostsRequest = fetchRecentPosts(10, 24)
      .then((posts) => {
        recentPosts.value = posts
      })
      .catch(() => {
        // Activity feed не должен ломать основную страницу.
      })
      .finally(() => {
        recentPostsRequest = null
      })
    return recentPostsRequest
  }

  async function loadPostDetail(postId: number) {
    try {
      selectedPost.value = await fetchPostDetail(postId)
      return selectedPost.value
    } catch (e) {
      ElMessage.error('Ошибка загрузки деталей поста')
      return null
    }
  }

  async function validatePost(postId: number, verdict: 'COMPLAINT' | 'FALSE_POS') {
    try {
      await submitValidation({ post_id: postId, verdict })
      ElMessage.success(`Пост #${postId} отмечен: ${verdictLabel(verdict)}`)

      // Обновляем статус в текущем списке.
      const nextStatus = verdict === 'COMPLAINT' ? 'VALIDATED' : 'ARCHIVED'
      const post = posts.value.find((p) => p.id === postId)
      if (post) {
        post.status = nextStatus
        if (!matchesActiveStatusFilter(nextStatus)) {
          posts.value = posts.value.filter((p) => p.id !== postId)
        }
      }
      if (selectedPost.value?.id === postId) {
        selectedPost.value.status = nextStatus
      }

      // Убираем пост из выделения.
      selectedIds.value.delete(postId)

      return true
    } catch (e) {
      return false
    }
  }

  async function validateBulk(verdict: 'COMPLAINT' | 'FALSE_POS') {
    const ids = Array.from(selectedIds.value)
    let success = 0

    for (const id of ids) {
      const ok = await validatePost(id, verdict)
      if (ok) success++
    }

    if (success > 0) {
      ElMessage.success(`${success} постов отмечено: ${verdictLabel(verdict)}`)
      selectedIds.value.clear()
      await loadPosts(true)
    }
  }

  function verdictLabel(verdict: string): string {
    switch (verdict) {
      case 'COMPLAINT': return 'Жалоба'
      case 'FALSE_POS': return 'Ложное срабатывание'
      default: return verdict
    }
  }

  function matchesActiveStatusFilter(status: string): boolean {
    if (!statusFilter.value || statusFilter.value === 'All') return true
    const allowed = statusFilter.value
      .split(',')
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean)
    return allowed.includes(status.toUpperCase())
  }

  function toggleSelection(id: number) {
    if (selectedIds.value.has(id)) {
      selectedIds.value.delete(id)
    } else {
      selectedIds.value.add(id)
    }
  }

  function clearSelection() {
    selectedIds.value.clear()
  }

  function nextPage() {
    page.value++
    loadPosts(true)
  }

  function prevPage() {
    if (page.value > 0) {
      page.value--
      loadPosts(true)
    }
  }

  function clearFilters() {
    searchQuery.value = ''
    categoryFilter.value = ''
    sourceFilter.value = ''
    statusFilter.value = 'FLAGGED'
    districtFilter.value = ''
    dateFrom.value = ''
    dateTo.value = ''
  }

  return {
    posts,
    recentPosts,
    selectedPost,
    loading,
    error,
    page,
    pageSize,
    hasMore,
    searchQuery,
    categoryFilter,
    sourceFilter,
    statusFilter,
    districtFilter,
    dateFrom,
    dateTo,
    selectedIds,
    filteredPosts,
    isSelected,
    loadPosts,
    refreshPosts,
    startAutoRefresh,
    stopAutoRefresh,
    loadRecentPosts,
    loadPostDetail,
    validatePost,
    validateBulk,
    toggleSelection,
    clearSelection,
    nextPage,
    prevPage,
    clearFilters,
  }
})
